import { Controller } from "@hotwired/stimulus"
import { CUSTOM_MAP_LAYER_STYLE } from "./style_defaults"
import * as myMap from "./maps"
import { resolveMapTypeId } from "./maps"
import { mapInfoPanel } from "./map_info_panel"
import { layerManager } from "./layer_manager"
import { resolveFeatureStyle } from "./feature_style"

export default class extends Controller {
  static targets = ["map", "layersData", "infoPanel", "infoPanelContent"]
  static values = {
    centerLat: { type: Number, default: 55.5 },
    centerLon: { type: Number, default: -4.6 },
    defaultZoom: { type: Number, default: 12 },
    defaultMapType: { type: String, default: 'terrain' }
  }

  async connect() {
    this.bounds = new google.maps.LatLngBounds()
    this.shapes = [] // Track all shapes with their metadata for click detection
    this.geojsonCache = {} // Cache fetched GeoJSON by URL to avoid duplicate requests
    this.initialized = false

    // Detect whether this page has layer toggle checkboxes — if so, delegate to layerManager
    this.useLayerManager = this.element.querySelectorAll('[data-layer]').length > 0

    // If inside a hidden tab pane, defer initialization until that specific tab is shown
    const tabPane = this.element.closest('.tab-pane')
    if (tabPane && !tabPane.classList.contains('show')) {
      this.tabShownHandler = (event) => {
        const targetPane = document.querySelector(event.target.getAttribute('data-bs-target') || event.target.getAttribute('href'))
        if (targetPane === tabPane) this.initializeMap()
      }
      document.addEventListener('shown.bs.tab', this.tabShownHandler)
      return
    }

    await this.initializeMap()
  }

  disconnect() {
    if (this.tabShownHandler) {
      document.removeEventListener('shown.bs.tab', this.tabShownHandler)
    }
  }

  async initializeMap() {
    if (this.initialized) return
    this.initialized = true

    // Clean up the tab listener if it exists
    if (this.tabShownHandler) {
      document.removeEventListener('shown.bs.tab', this.tabShownHandler)
      this.tabShownHandler = null
    }

    // Pre-calculate bounds from layer data before creating map
    await this.precalculateBounds()

    // Initialize map with correct bounds (no animation needed)
    this.initMap()

    // Setup info panel for layer feature information
    if (this.hasInfoPanelTarget && this.hasInfoPanelContentTarget) {
      mapInfoPanel.setup(this, this.infoPanelTarget, this.infoPanelContentTarget, this.map)
    }

    if (this.useLayerManager) {
      // Delegate all layer rendering and toggling to layerManager
      await layerManager.initializeLayers(this.map, this)
    } else {
      // Standalone rendering (used by tab view and static map layers page)
      await this.loadAllLayers()
    }
  }

  // Toggle layer visibility via layerManager (used when toggles are present)
  async toggleLayer(event) {
    await layerManager.handleToggleLayer(event)
  }

  closeInfoPanel() {
    mapInfoPanel.close()
  }

  async precalculateBounds() {
    if (!this.hasLayersDataTarget || !this.layersDataTarget.textContent.trim()) {
      return
    }

    let layers
    try {
      layers = JSON.parse(this.layersDataTarget.textContent)
    } catch (error) {
      return
    }

    // Fetch all GeoJSON and calculate bounds
    for (const layerData of layers) {
      let geojson

      if (layerData.geojson_url) {
        try {
          const response = await fetch(layerData.geojson_url)
          if (response.ok) {
            geojson = await response.json()
            this.geojsonCache[layerData.geojson_url] = geojson
          }
        } catch (error) {
          // Silently handle fetch errors
        }
      } else if (layerData.geojson) {
        geojson = layerData.geojson
      }

      if (geojson && geojson.features) {
        geojson.features.forEach(feature => {
          this.extractBoundsFromFeature(feature)
        })
      }
    }
  }

  extractBoundsFromFeature(feature) {
    const geometry = feature.geometry

    if (geometry.type === 'Polygon') {
      geometry.coordinates[0].forEach(coord => {
        this.bounds.extend({ lat: coord[1], lng: coord[0] })
      })
    } else if (geometry.type === 'MultiPolygon') {
      geometry.coordinates.forEach(polygon => {
        polygon[0].forEach(coord => {
          this.bounds.extend({ lat: coord[1], lng: coord[0] })
        })
      })
    }
  }

  initMap() {
    const mapType = resolveMapTypeId(this.defaultMapTypeValue)
    let mapOptions
    if (!this.bounds.isEmpty()) {
      // Create map centered on the bounds
      const center = this.bounds.getCenter()
      mapOptions = myMap.defaultMapOptions(center.lat(), center.lng(), this.defaultZoomValue, mapType)
    } else {
      // Fallback to default center/zoom when no layers (consistent with other map pages)
      mapOptions = myMap.defaultMapOptions(this.centerLatValue, this.centerLonValue, this.defaultZoomValue, mapType)
    }

    this.map = new google.maps.Map(this.mapTarget, mapOptions)

    // Fit to bounds immediately (no animation since map just created)
    if (!this.bounds.isEmpty()) {
      this.map.fitBounds(this.bounds)

      // Limit zoom
      google.maps.event.addListenerOnce(this.map, 'idle', () => {
        if (this.map.getZoom() > 15) {
          this.map.setZoom(15)
        }
      })
    }
  }

  // --- Standalone rendering (fallback when layerManager not used) ---

  async loadAllLayers() {
    if (!this.hasLayersDataTarget || !this.layersDataTarget.textContent.trim()) {
      return
    }

    let layers
    try {
      layers = JSON.parse(this.layersDataTarget.textContent)
    } catch (error) {
      return
    }

    // Load each layer (bounds already calculated, just drawing now)
    for (const layerData of layers) {
      await this.loadLayer(layerData)
    }
  }

  async loadLayer(layerData) {
    let geojson

    if (layerData.geojson_url) {
      try {
        if (this.geojsonCache[layerData.geojson_url]) {
          geojson = this.geojsonCache[layerData.geojson_url]
        } else {
          const response = await fetch(layerData.geojson_url)
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`)
          }
          geojson = await response.json()
          this.geojsonCache[layerData.geojson_url] = geojson
        }
      } catch (error) {
        return
      }
    }
    else if (layerData.geojson) {
      geojson = layerData.geojson
    }
    else {
      return
    }

    if (!geojson || !geojson.features) {
      return
    }

    geojson.features.forEach(feature => {
      this.drawFeature(feature, layerData)
    })
  }

  drawFeature(feature, layerData) {
    const properties = feature.properties || {}
    const geometry = feature.geometry
    const { shapeStyle, metadata } = resolveFeatureStyle(layerData, properties)

    if (geometry.type === 'Polygon') {
      this.drawPolygon(geometry.coordinates, shapeStyle, metadata)
    } else if (geometry.type === 'MultiPolygon') {
      geometry.coordinates.forEach(polygonCoords => {
        this.drawPolygon(polygonCoords, shapeStyle, metadata)
      })
    }
  }

  drawPolygon(coordinates, shapeStyle, metadata) {
    const paths = coordinates.map(ring => {
      return ring.map(coord => {
        const latlng = { lat: coord[1], lng: coord[0] }
        this.bounds.extend(latlng)
        return latlng
      })
    })

    const polygon = new google.maps.Polygon({
      paths: paths,
      strokeColor: shapeStyle.strokeColor,
      fillColor: shapeStyle.fillColor,
      fillOpacity: shapeStyle.fillOpacity,
      strokeWeight: shapeStyle.strokeWeight,
      strokeOpacity: shapeStyle.strokeOpacity,
      map: this.map
    })

    // Store shape with metadata for click detection
    if (metadata.featureName || metadata.description) {
      this.shapes.push({ shape: polygon, type: 'polygon', metadata })
      polygon.addListener('click', (event) => this.handleShapeClick(event))
    }
  }

  handleShapeClick(event) {
    const clickLatLng = event.latLng
    const featuresAtPoint = []

    // Find all shapes that contain the click point
    this.shapes.forEach(({ shape, type, metadata }) => {
      let containsPoint = false

      if (type === 'polygon') {
        containsPoint = google.maps.geometry.poly.containsLocation(clickLatLng, shape)
      }

      if (containsPoint) {
        featuresAtPoint.push(metadata)
      }
    })

    if (featuresAtPoint.length > 0) {
      const customEvent = new CustomEvent('map-features-clicked', {
        detail: {
          features: featuresAtPoint,
          position: { lat: clickLatLng.lat(), lng: clickLatLng.lng() }
        },
        bubbles: true
      })
      document.dispatchEvent(customEvent)
    }
  }
}
